import React, { useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { ProjectsListSkeleton } from '../../../LoadersSkeletons/ProjectsListSkeleton/ProjectsListSkeleton';
import { ReactComponent as IconSort } from '../../../../assets/icons/u_sort.svg';
import PaginationTable from '../../PaginationTable/PaginationTable';
import { Pagination } from '@material-ui/lab';
import usePaginationSort from '../../../../hooks/usePaginationSort';
import useTable from '../../../../hooks/useTable';
import useDataNormalize from '../../../../hooks/useDataNormalize';
import { ExcelDownloadButton } from '../../../Util/ExcelDownloadButton/ExcelDownloadButton';
import { IndicatorKeyEnum } from '../../../../models/IndicatorKeyEnum';
import { renderDateOrSlash } from '../../../../services/stringUtil';
import { Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CriteriaType } from '../../../../models/PaginationIndicator';
import { FilterTables } from '../../../Filter/FilterTables/FilterTables';
import { SelectFilterData } from './SelectFilterData';
import NumberFormat from 'react-number-format';
import SearchTabErrorMessage from '../../../Util/Errors/SearchTabErrorMessage/SearchTabErrorMessage';

const headers = [
  'new_period',
  'code',
  'operation',
  'name',
  'division',
  'new_orginila_projection',
  'new_current_projection',
  'amount_disbursed',
  'new_preview_of_the_current_projection',
];

const selectedKeys = ['field5', 'code', 'loanNumber', 'title', 'field7', 'field1', 'field2', 'field3', 'field9'];

type Props = {
  dataTable: any;
  fnChangeData: (month, year) => void;
  labelFilterDefault?: string;
  disabledFilter: boolean;
  loadingData: boolean;
};

interface Projection {
  projectionOriginal: number;
  projectionCurrent: number;
  projectionDisburment: number;
}

export default function DisbursementsAndProjectsForTheYearTable({
  dataTable,
  fnChangeData,
  labelFilterDefault,
  disabledFilter,
  loadingData,
}: Props) {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [projecctionData, setProjecctionData] = useState<Projection>();
  const { projects } = useTable();
  const [dataFilter, setDataFilter] = useState<any>(projects);
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (dataTable) {
      setDataFilter(dataTable);
      totalProjectionTable(dataTable);
    }
  }, [dataTable]);

  const { handlePaginationChange, handleSort, currentList, pagesCount, currentPage } = usePaginationSort({
    projects: dataFilter,
    defaultSortCriteria: 'code',
    pageCount: rowsPerPage,
  });

  const totalProjectionTable = dataTable => {
    const orgininal = dataTable.reduce((acc, value) => acc + Number(value.field1), 0);
    const current = dataTable.reduce((acc, value) => acc + Number(value.field2), 0);
    const disburment = dataTable.reduce((acc, value) => acc + Number(value.field3), 0);

    setProjecctionData({
      projectionOriginal: orgininal,
      projectionCurrent: current,
      projectionDisburment: disburment,
    });
  };

  const sortTable = (keyToSort, fieldType) => {
    let criteriaType;
    switch (fieldType) {
      case 'numeric':
      case 'money':
      case 'percentage':
      case 'no_round_two_decimal':
        criteriaType = CriteriaType.number;
        break;
      case 'date':
        criteriaType = CriteriaType.date;
        break;
      default:
        criteriaType = CriteriaType.string;
        break;
    }
    handleSort(keyToSort, criteriaType);
  };

  const BlackTooltip = withStyles({
    tooltip: {
      backgroundColor: '#101319',
      fontSize: '16px',
      fontFamily: 'Rubik',
      lineHeight: '20px',
      padding: '8px 12px',
      borderRadius: '8px',
    },
    arrow: {
      fontSize: 20,
      color: '#4A4A4A',
      '&::before': {
        backgroundColor: '#101319',
      },
    },
  })(Tooltip);

  const data = useDataNormalize(headers, selectedKeys, dataFilter);

  return (
    <>
      {loadingData ? (
        <ProjectsListSkeleton />
      ) : (
        <div className="indicator-detail-table-container">
          <div className="container-left">
            <div className="filter-style">
              <SelectFilterData
                onChangeData={fnChangeData}
                labelFilterDefault={labelFilterDefault}
                disabledFilter={disabledFilter}
              />
            </div>
            <div className="container-line">
              <div className="division-style"></div>
            </div>
            <div className="data-container">
              <section className='statics-container'>
                <div className="original-proyect">
                  <p>
                    <b>
                      <NumberFormat
                        value={projecctionData?.projectionOriginal}
                        displayType={'text'}
                        thousandSeparator={'.'}
                        decimalSeparator={','}
                        decimalScale={1}
                      />{' '}
                      <span>USD</span>
                    </b>
                  </p>
                  <p><span></span> {t('new_orginila_projection')}</p>
                </div>
                <div className="current-proyect">
                  <p>
                    <b>
                      <NumberFormat
                        value={projecctionData?.projectionCurrent}
                        displayType={'text'}
                        thousandSeparator={'.'}
                        decimalSeparator={','}
                        decimalScale={1}
                      />{' '}
                      <span>USD</span>
                    </b>
                  </p>
                  <p><span></span> {t('new_current_projection')}</p>
                </div>
                <div className="amount-disbursed">
                  <p>
                    <b>
                      <NumberFormat
                        value={projecctionData?.projectionDisburment}
                        displayType={'text'}
                        thousandSeparator={'.'}
                        decimalSeparator={','}
                        decimalScale={1}
                      />{' '}
                      <span>USD</span>
                    </b>
                  </p>
                  <p><span></span> {t('amount_disbursed')}</p>
                </div>
              </section>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                <ExcelDownloadButton headers={headers} selectedKeys={selectedKeys} dataExcel={data} />
                <FilterTables
                  projects={dataTable}
                  keysFilter={selectedKeys}
                  fnDataFilter={setDataFilter}
                  disabledNumber={currentPage}
                  indicatorKey={IndicatorKeyEnum.DISBURSEMENTS_AND_PROJECTIONS_FOR_THE_YEAR}
                  onSearchChange={setSearchTerm}
                />
              </div>
            </div>
          </div>

          <TableContainer>
            <div className="border-table">
              <Table>
                <TableHead>
                  <TableRow style={{ maxHeight: '88px', minHeight: '88px', height: '88px' }}>
                    {dataFilter?.length > 0 ? (
                      <>
                        <TableCell onClick={() => sortTable('field8', 'string')}>
                          <div className="text-head">
                            {t('new_period')} <IconSort className="sortIcon" />
                          </div>
                        </TableCell>
                        <TableCell onClick={() => sortTable('code', 'string')}>
                          <div className="text-head">
                            {t('code')} <IconSort className="sortIcon" />
                          </div>
                        </TableCell>
                        <TableCell onClick={() => sortTable('loanNumber', 'string')}>
                          <div className="text-head">
                            {t('operation')} <IconSort className="sortIcon" />
                          </div>
                        </TableCell>
                        <TableCell onClick={() => sortTable('title', 'string')}>
                          <div className="text-head">
                            {t('name')} <IconSort className="sortIcon" />
                          </div>
                        </TableCell>
                        <TableCell onClick={() => sortTable('field7', 'string')}>
                          <div className="text-head">
                            {t('division')} <IconSort className="sortIcon" />
                          </div>
                        </TableCell>
                        <TableCell onClick={() => sortTable('field1', 'numeric')}>
                          <div className="text-head">
                            {t('new_orginila_projection')} <IconSort className="sortIcon" />
                          </div>
                        </TableCell>
                        <TableCell onClick={() => sortTable('field2', 'numeric')}>
                          <div className="text-head purchases_made">
                            {t('new_current_projection')} <IconSort className="sortIcon" />
                          </div>
                        </TableCell>
                        <TableCell onClick={() => sortTable('field3', 'numeric')}>
                          <div className="text-head">
                            {t('amount_disbursed')} <IconSort className="sortIcon" />
                          </div>
                        </TableCell>
                        <TableCell onClick={() => sortTable('field9', 'numeric')}>
                          <div className="text-head">
                            {t('new_preview_of_the_current_projection')} <IconSort className="sortIcon" />
                          </div>
                        </TableCell>
                      </>
                    ) : (
                      <TableCell onClick={() => sortTable('field9', 'numeric')}>
                        <div className="text-head">
                        </div>
                      </TableCell>
                    )}

                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataFilter?.length > 0 ? (
                    currentList?.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{t(row.field8)}</TableCell>
                        <TableCell className="link-project">
                          {row.field10 == '1' ? (
                            <BlackTooltip title={t('go_to_project_info')} arrow>
                              <Link style={{ color: '#3D3E40' }} to={`/projectdetail?id=${row.id}`}>
                                {row.code}
                              </Link>
                            </BlackTooltip>
                          ) : (
                            <p
                              style={{
                                fontFamily: "'Rubik', sans-serif",
                                color: '#4a4a4a',
                                fontSize: '16px',
                                fontWeight: 400,
                                boxSizing: 'border-box',
                                textDecoration: 'none',
                              }}
                            >
                              {row.code}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>{row.loanNumber}</TableCell>
                        <TableCell className="name-project">
                          <BlackTooltip title={row.title} placement="bottom" enterDelay={500} leaveDelay={100} arrow>
                            <p className=" name-project-nowrap"> {row.title}</p>
                          </BlackTooltip>
                        </TableCell>
                        <TableCell>{row.field7}</TableCell>
                        <TableCell>
                          <NumberFormat
                            value={row.field1}
                            displayType={'text'}
                            thousandSeparator={','}
                            decimalSeparator={'.'}
                            decimalScale={1}
                          />
                        </TableCell>
                        <TableCell>
                          <NumberFormat
                            value={row.field2}
                            displayType={'text'}
                            thousandSeparator={','}
                            decimalSeparator={'.'}
                            decimalScale={1}
                          />
                        </TableCell>
                        <TableCell>
                          <NumberFormat
                            value={row.field3}
                            displayType={'text'}
                            thousandSeparator={','}
                            decimalSeparator={'.'}
                            decimalScale={1}
                          />
                        </TableCell>
                        <TableCell><span className="porcent">{row.field9 === '-' ? row.field9 : `${Math.trunc(row.field9)} %`}</span></TableCell>
                      </TableRow>
                    ))
                  ) : searchTerm.length >= 3 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length}>
                        <SearchTabErrorMessage search={searchTerm} />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </div>
          </TableContainer>

          <div className="pagination-style">
            <PaginationTable fnSetRows={setRowsPerPage} rows={rowsPerPage} numProyects={dataFilter?.length} />
            {dataFilter?.length !== 0 ? (
              <Pagination count={pagesCount} page={currentPage} onChange={handlePaginationChange} />
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
